import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  CHART_COLORS,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chart-theme';

export interface DonutItem {
  name: string;
  value: number;
}

interface DonutChartProps {
  data: DonutItem[];
  height?: number;
  centerLabel?: string;
}

export function DonutChart({
  data,
  height = 240,
  centerLabel,
}: DonutChartProps): JSX.Element {
  const total = useMemo(
    () => data.reduce((sum, d) => sum + d.value, 0),
    [data],
  );
  const labelText = centerLabel ?? 'Total';

  return (
    <div className="flex items-center gap-6">
      <div className="flex-1" style={{ minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="65%"
              outerRadius="90%"
              stroke="#000000"
              strokeWidth={2}
              isAnimationActive={false}
              startAngle={90}
              endAngle={-270}
            >
              {data.map((_, i) => (
                <Cell
                  key={i}
                  fill={CHART_COLORS[i % CHART_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={CHART_TOOLTIP_STYLE}
              itemStyle={CHART_TOOLTIP_ITEM_STYLE}
              cursor={false}
              formatter={(value: number, name: string) => [
                `${value.toLocaleString()} (${pct(value, total)})`,
                name,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="min-w-[140px] space-y-2 text-sm">
        <div className="text-xs font-bold uppercase tracking-wide text-fg-muted">
          {labelText}
        </div>
        <div className="text-[28px] font-extrabold leading-none tabular-nums">
          {total.toLocaleString()}
        </div>
        <ul className="pt-2 space-y-1.5">
          {data.map((d, i) => (
            <li
              key={d.name}
              className="flex items-center justify-between gap-3"
            >
              <span className="flex items-center gap-2 text-fg-muted">
                <span
                  className="block h-2 w-2 rounded-full"
                  style={{
                    background:
                      CHART_COLORS[i % CHART_COLORS.length],
                  }}
                />
                {d.name}
              </span>
              <span className="text-fg font-medium tabular-nums">
                {d.value.toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function pct(part: number, total: number): string {
  if (total === 0) return '0%';
  return `${((part / total) * 100).toFixed(1)}%`;
}
